// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Checkbox, { CheckboxPresentationProps } from "@plumberssupply/MobiusOverrides/Checkbox/Checkbox";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities/mergeWithCss";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    isChecked?: boolean;
    sizeVariant?: "small" | "default";
    toggleHandler: (value: boolean) => void;
    label?: string;
    tooltip?: string;
    isLoading?: boolean;
    extendedStyles?: RoundToBoxStyles;
}

type Props = OwnProps;

export interface RoundToBoxStyles {
    wrapper?: InjectableCss;
    checkbox?: CheckboxPresentationProps;
    typography?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const roundToBoxStyles: RoundToBoxStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    tooltip: {
        iconProps: {
            size: 12,
        },
    },
};

const RoundToBox = ({ isChecked, sizeVariant, toggleHandler, label, tooltip, isLoading, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeWithCss(roundToBoxStyles, extendedStyles));
    const [checked, setChecked] = useState(isChecked);

    useEffect(() => {
        setChecked(isChecked);
    }, [isChecked]);

    const handleCheckboxToggle = (event: SyntheticEvent<Element, Event>, value: boolean) => {
        setChecked(value);
        toggleHandler?.(value);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Checkbox
                sizeVariant={sizeVariant || "small"}
                onChange={handleCheckboxToggle}
                checked={checked}
                {...styles.checkbox}
            >
                <Typography {...styles.typography}>{label || translate("Box?")}</Typography>
            </Checkbox>
            <Tooltip text={tooltip || siteMessage("Round_To_Box").toString()} {...styles.tooltip} />
            {isLoading && <LoadingSpinner size={20} />}
        </StyledWrapper>
    );
};

export default RoundToBox;
